import { useCallback, useMemo, useState } from 'react';

import { AnimatedAnswerContainer } from './components/AnimatedAnswerContainer';
import { NavigationFooter } from './components/NavigationFooter';
import { ProgressBar } from './components/ProgressBar';
import { QuestionContent } from './components/QuestionContent';

import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReactNode } from 'react';

import { QuestionType } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

export type SurveyAnswerContainerProps = {
    question: ReviewQuestion;
    renderAnswer: ReactNode | (() => ReactNode);
    isFirst: boolean;
    isLast: boolean;
    onNext: () => void;
    onPrevious?: () => void;
    onSkip?: () => void;
    onSubmit?: () => void;
    isPreview?: boolean;
    backgroundImageUrl?: string;
    showComment?: boolean;
    commentValue?: string;
    onCommentChange?: (value: string) => void;
    commentLabel?: string;
    enableTransitions?: boolean;
    transitionDurationMs?: number;
    transitionDirection?: 'next' | 'previous';
    contentKey?: string | number;
    currentIndex?: number;
    totalQuestions?: number;
    className?: string;
    reviewee?: User;
    showScoresToTheReviewee?: boolean;
    showCommentsToTheReviewee?: boolean;
    includeOther?: boolean;
    isOtherSelected?: boolean;
    otherText?: string;
    onOtherTextChange?: (text: string) => void;
    disabled?: boolean;
    dataTestId?: string;
    isAnswerValid?: boolean;
    isSaving?: boolean;
};

const DEFAULT_DURATION = 280;
const MIN_CONTENT_HEIGHT_PX = 580;
const PROGRESS_BAR_GRADIENT = 'bg-gradient-purple';
const PROGRESS_COMPLETION_ANIMATION_MS = 800;
const MAX_OTHER_TEXT_LENGTH = 50;
const BACKGROUND_BLUR_PX = 5;
const BACKGROUND_BLUR_SCALE = 1.03;
const BACKGROUND_GRADIENT_OVERLAY = `linear-gradient(0deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.65) 100%), linear-gradient(218deg, rgba(255, 170, 145, 0.92) 5%, rgba(151, 71, 255, 0.78) 89.54%)`;

export const SurveyAnswerContainer = ({
    question,
    renderAnswer,
    isLast,
    onNext,
    onPrevious,
    onSkip,
    onSubmit,
    backgroundImageUrl,
    showComment,
    commentValue,
    onCommentChange,
    commentLabel,
    enableTransitions = true,
    transitionDurationMs = DEFAULT_DURATION,
    transitionDirection = 'next',
    contentKey,
    currentIndex = 0,
    totalQuestions = 1,
    className = '',
    reviewee,
    showScoresToTheReviewee,
    showCommentsToTheReviewee,
    isPreview = false,
    includeOther = false,
    isOtherSelected = false,
    otherText = '',
    onOtherTextChange,
    disabled = false,
    dataTestId,
    isAnswerValid = true,
    isSaving = false,
}: SurveyAnswerContainerProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const managerLabel = getManagerLabel();

    const [isFinishing, setIsFinishing] = useState(false);

    const showCharLimitWarning = (otherText?.length ?? 0) >= MAX_OTHER_TEXT_LENGTH;

    const answerContent = useMemo(
        () => (typeof renderAnswer === 'function' ? renderAnswer() : renderAnswer),
        [renderAnswer],
    );

    const confidentialityMessage = useMemo(() => {
        if (showScoresToTheReviewee === undefined && showCommentsToTheReviewee === undefined) {
            return t('answer.confidentiality.noResults', { managerLabel });
        }

        if (showScoresToTheReviewee === false && showCommentsToTheReviewee === false) {
            return t('answer.confidentiality.noResults', { managerLabel });
        }

        if (showScoresToTheReviewee && !showCommentsToTheReviewee) {
            return t('answer.confidentiality.scoresOnly', { managerLabel });
        }

        return t('answer.confidentiality.scoresAndComments', { managerLabel });
    }, [showScoresToTheReviewee, showCommentsToTheReviewee, managerLabel, t]);

    const handleOtherTextChange = useCallback(
        (value: string) => {
            const trimmedValue = value.trimStart();
            onOtherTextChange?.(trimmedValue);
        },
        [onOtherTextChange],
    );

    const handleNext = useCallback(() => {
        // isFinishing blocks re-entry until the deferred onSubmit has executed.
        if (isFinishing) {
            return;
        }
        if (isLast && onSubmit) {
            if (isAnswerValid) {
                setIsFinishing(true);
                setTimeout(onSubmit, PROGRESS_COMPLETION_ANIMATION_MS);
            } else {
                onSubmit();
            }
            return;
        }
        onNext();
    }, [isLast, onSubmit, onNext, isAnswerValid, isFinishing]);

    const progressPercentage = Math.round((currentIndex / totalQuestions) * 100);
    const displayedProgress = isFinishing ? 100 : progressPercentage;

    const wrapperStyle = { minHeight: `${MIN_CONTENT_HEIGHT_PX}px` };

    const blurredImageStyle = backgroundImageUrl
        ? {
              backgroundImage: `url('${backgroundImageUrl}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              filter: `blur(${BACKGROUND_BLUR_PX}px)`,
              transform: `scale(${BACKGROUND_BLUR_SCALE})`,
          }
        : undefined;

    const gradientOverlayStyle = backgroundImageUrl
        ? { backgroundImage: BACKGROUND_GRADIENT_OVERLAY }
        : undefined;

    return (
        <div
            className={`rounded-[40px] border border-white bg-white/45 shadow-[0_45px_120px_rgba(65,36,118,0.2)] backdrop-blur-sm ${className}`}
        >
            <div className={'survey-container-padding'}>
                <div
                    className={
                        'relative w-full overflow-hidden rounded-[30px] shadow-[0_35px_80px_rgba(0,0,0,0.45)]'
                    }
                    style={wrapperStyle}
                >
                    {backgroundImageUrl && (
                        <>
                            <div
                                aria-hidden
                                className={'absolute inset-0'}
                                style={blurredImageStyle}
                            />

                            <div
                                aria-hidden
                                className={'absolute inset-0'}
                                style={gradientOverlayStyle}
                            />
                        </>
                    )}

                    {question.questionType !== QuestionType.SECTION_TITLE ? (
                        <div
                            className={
                                'relative flex flex-col items-center justify-center px-6 py-20 sm:px-12'
                            }
                            style={{ minHeight: MIN_CONTENT_HEIGHT_PX }}
                        >
                            <AnimatedAnswerContainer
                                question={question}
                                transitionDurationMs={transitionDurationMs}
                                transitionDirection={transitionDirection}
                                contentKey={contentKey}
                                enableTransitions={enableTransitions}
                            >
                                <QuestionContent
                                    question={question}
                                    answerContent={answerContent}
                                    confidentialityMessage={confidentialityMessage}
                                    reviewee={reviewee}
                                    showComment={showComment}
                                    commentValue={commentValue}
                                    commentLabel={commentLabel}
                                    onCommentChange={onCommentChange}
                                    onNext={handleNext}
                                    includeOther={includeOther}
                                    isOtherSelected={isOtherSelected}
                                    otherText={otherText}
                                    onOtherTextChange={handleOtherTextChange}
                                    showCharLimitWarning={showCharLimitWarning}
                                    maxOtherTextLength={MAX_OTHER_TEXT_LENGTH}
                                    disabled={disabled}
                                    dataTestId={dataTestId}
                                />
                            </AnimatedAnswerContainer>
                        </div>
                    ) : (
                        <div
                            className={
                                'relative flex flex-1 items-center justify-center text-white'
                            }
                            style={{ minHeight: MIN_CONTENT_HEIGHT_PX }}
                        >
                            <AnimatedAnswerContainer
                                question={question}
                                transitionDurationMs={transitionDurationMs}
                                transitionDirection={transitionDirection}
                                contentKey={contentKey}
                                enableTransitions={enableTransitions}
                            >
                                {answerContent}
                            </AnimatedAnswerContainer>
                        </div>
                    )}

                    <ProgressBar
                        progressPercentage={displayedProgress}
                        gradientClassName={PROGRESS_BAR_GRADIENT}
                    />
                </div>

                <NavigationFooter
                    isLast={isLast}
                    isPreview={isPreview}
                    onNext={handleNext}
                    onPrevious={onPrevious}
                    onSkip={onSkip}
                    isSaving={isSaving || isFinishing}
                />
            </div>
        </div>
    );
};

export default SurveyAnswerContainer;
